import React, { useState, useRef, useEffect, useCallback } from "react";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functions } from "../firebase";
import {
    FiUpload, FiScissors, FiPlay, FiPause, FiTrash2, FiImage, FiX, FiRefreshCw, FiCrop,
    FiZoomIn, FiZoomOut,
} from "react-icons/fi";

// ─── Design constants (matches AddLesson/EditLesson) ──────────────────
const TEAL = "#2a9d8f";
const RED = "#e76f51";
const WAVE_HEIGHT = 84;
const MIN_ZOOM = 1;
const MAX_ZOOM = 40;

const INPUT_STYLE = {
    fontFamily: "'Quicksand', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: 10,
    border: "1.5px solid #E0E0E0",
    background: "#f7f9fb",
    width: "100%",
    outline: "none",
    color: "#000",
    boxSizing: "border-box",
    resize: "vertical",
};

function uid() {
    return Math.random().toString(36).slice(2, 10);
}

function formatTime(t) {
    if (!isFinite(t) || t < 0) return "0:00.0";
    const m = Math.floor(t / 60);
    const s = (t % 60).toFixed(1).padStart(4, "0");
    return `${m}:${s}`;
}

function computePeaksFromBuffer(audioBuffer, numPeaks = 400) {
    const channelData = audioBuffer.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(channelData.length / numPeaks));
    const peaks = [];
    for (let i = 0; i < numPeaks; i++) {
        const start = i * blockSize;
        let sum = 0, count = 0;
        for (let j = 0; j < blockSize && start + j < channelData.length; j++) {
            sum += Math.abs(channelData[start + j]);
            count++;
        }
        peaks.push(count ? sum / count : 0);
    }
    const max = Math.max(...peaks, 0.0001);
    return { peaks: peaks.map(p => p / max), duration: audioBuffer.duration };
}

// Returns the decoded AudioBuffer alongside the peaks — the buffer stays
// valid after ctx.close() (it's a plain data container), so callers that
// need to actually edit the audio (trim/cut) can hang onto it.
async function decodeAudioArrayBuffer(arrayBuffer, numPeaks = 400) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    try {
        const audioBuffer = await new Promise((resolve, reject) => {
            ctx.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
        });
        const { peaks, duration } = computePeaksFromBuffer(audioBuffer, numPeaks);
        return { peaks, duration, audioBuffer };
    } finally {
        ctx.close();
    }
}

// Builds a new AudioBuffer with the given [start,end] second ranges excised
// and the remaining audio spliced back together — used both for cutting a
// manually-selected range and for auto-trimming detected silence.
function removeRangesFromBuffer(buffer, ranges) {
    const sr = buffer.sampleRate;
    const nCh = buffer.numberOfChannels;
    const total = buffer.length;
    const sampleRanges = ranges
        .map(r => ({
            start: Math.max(0, Math.min(total, Math.round(r.start * sr))),
            end: Math.max(0, Math.min(total, Math.round(r.end * sr))),
        }))
        .filter(r => r.end > r.start)
        .sort((a, b) => a.start - b.start);

    const removedSamples = sampleRanges.reduce((s, r) => s + (r.end - r.start), 0);
    const keepLength = Math.max(1, total - removedSamples);
    const newBuffer = new AudioBuffer({ numberOfChannels: nCh, length: keepLength, sampleRate: sr });

    for (let ch = 0; ch < nCh; ch++) {
        const src = buffer.getChannelData(ch);
        const dst = newBuffer.getChannelData(ch);
        let dstIdx = 0, srcIdx = 0;
        for (const r of sampleRanges) {
            dst.set(src.subarray(srcIdx, r.start), dstIdx);
            dstIdx += r.start - srcIdx;
            srcIdx = r.end;
        }
        dst.set(src.subarray(srcIdx, total), dstIdx);
    }
    return newBuffer;
}

// Finds the [start,end] window that contains the actual speech by scanning
// short windows for the loudest peak and cutting anything below a fraction
// of the recording's overall peak, with a little padding kept on each side.
function detectSilenceEdges(buffer, { thresholdRatio = 0.035, windowMs = 20, paddingMs = 120 } = {}) {
    const sr = buffer.sampleRate;
    const windowSize = Math.max(1, Math.round((sr * windowMs) / 1000));
    const nCh = buffer.numberOfChannels;
    const total = buffer.length;
    const numWindows = Math.ceil(total / windowSize);
    const windowPeaks = new Float32Array(numWindows);
    let overallPeak = 0;
    for (let ch = 0; ch < nCh; ch++) {
        const data = buffer.getChannelData(ch);
        for (let w = 0; w < numWindows; w++) {
            const s = w * windowSize, e = Math.min(total, s + windowSize);
            let m = windowPeaks[w];
            for (let i = s; i < e; i++) {
                const v = Math.abs(data[i]);
                if (v > m) m = v;
            }
            windowPeaks[w] = m;
            if (m > overallPeak) overallPeak = m;
        }
    }
    if (overallPeak < 1e-5) return null;
    const threshold = overallPeak * thresholdRatio;
    let first = 0;
    while (first < numWindows && windowPeaks[first] < threshold) first++;
    let last = numWindows - 1;
    while (last >= 0 && windowPeaks[last] < threshold) last--;
    if (first > last) return null;
    const padding = paddingMs / 1000;
    return {
        start: Math.max(0, (first * windowSize) / sr - padding),
        end: Math.min(buffer.duration, ((last + 1) * windowSize) / sr + padding),
    };
}

// Encodes an AudioBuffer as a 16-bit PCM WAV Blob for re-upload after a trim.
function encodeWav(audioBuffer) {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const numFrames = audioBuffer.length;
    const blockAlign = numChannels * 2;
    const dataSize = numFrames * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (offset, str) => {
        for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    writeString(36, "data");
    view.setUint32(40, dataSize, true);

    const channels = [];
    for (let ch = 0; ch < numChannels; ch++) channels.push(audioBuffer.getChannelData(ch));

    let offset = 44;
    for (let i = 0; i < numFrames; i++) {
        for (let ch = 0; ch < numChannels; ch++) {
            const sample = Math.max(-1, Math.min(1, channels[ch][i]));
            view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
            offset += 2;
        }
    }
    return new Blob([buffer], { type: "audio/wav" });
}

// Shifts/collapses segment start & end times to account for removed ranges,
// then drops any segment that collapsed to zero length — trimming never adds
// a new part, it only tightens the audio the existing parts point to.
function remapSegmentsAfterRemoval(segments, ranges) {
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const remap = (t) => {
        let shift = 0;
        for (const r of sorted) {
            if (t <= r.start) break;
            if (t >= r.end) shift += (r.end - r.start);
            else return r.start - shift;
        }
        return t - shift;
    };
    return segments
        .map(s => ({ ...s, start: remap(s.start), end: remap(s.end) }))
        .filter(s => s.end - s.start > 0.02);
}

async function uploadAudioFile(file, lessonKey) {
    const path = `basics_audio/${lessonKey}/${Date.now()}_${file.name}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file, { contentType: file.type || "audio/mpeg" });
    return getDownloadURL(r);
}

async function uploadImageFile(file, lessonKey, segId) {
    const path = `basics_images/${lessonKey}/${segId}_${Date.now()}_${file.name}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file, { contentType: file.type || "image/jpeg" });
    return getDownloadURL(r);
}

function extractStoragePath(url) {
    const match = url.match(/\/o\/([^?]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

function base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
}

// The Storage bucket has no CORS policy, so a plain fetch() against a
// download URL gets blocked cross-origin from the CMS's own origin (this is
// what surfaces as "Trim failed: Failed to fetch"). Try the direct fetch
// first since it's cheap and works for a file just uploaded in this tab,
// then fall back to the fetchBasicsAudio Cloud Function, which reads the
// file server-side (Admin SDK, no CORS involved) and returns it base64-encoded.
async function fetchAudioArrayBuffer(url) {
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("bad response");
        return await res.arrayBuffer();
    } catch {
        const path = extractStoragePath(url);
        if (!path) throw new Error("Could not determine the recording's storage path.");
        const fetchBasicsAudio = httpsCallable(functions, "fetchBasicsAudio");
        const result = await fetchBasicsAudio({ path });
        const base64 = result?.data?.base64;
        if (!base64) throw new Error("Server returned no audio data.");
        return base64ToArrayBuffer(base64);
    }
}

// ─── Small shared bits ─────────────────────────────────────────────────
function Spinner({ size = 14 }) {
    return (
        <>
            <style>{`@keyframes voSpin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: size, height: size, border: "2px solid #E0E0E0", borderTop: "2px solid #2a9d8f", borderRadius: "50%", animation: "voSpin 0.8s linear infinite" }} />
        </>
    );
}

function MiniBtn({ onClick, disabled, title, children, active }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            style={{
                width: 26, height: 26, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                border: `1.5px solid ${active ? TEAL : "#E0E0E0"}`, background: active ? "#2a9d8f18" : "#fff",
                color: active ? TEAL : "#555", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.35 : 1,
                flexShrink: 0,
            }}
        >
            {children}
        </button>
    );
}

// ─── Waveform (canvas) ──────────────────────────────────────────────────
function Waveform({
    peaks, duration, segments, currentTime, activeSegIdx, onSeek, onDragBoundary, onDragEnd,
    trimMode, trimSelection, onTrimSelectionChange,
}) {
    const scrollRef = useRef(null);
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [visibleWidth, setVisibleWidth] = useState(600);
    const [zoom, setZoom] = useState(1);
    const width = Math.round(visibleWidth * zoom);
    const draggingRef = useRef(null);
    const trimSelectionRef = useRef(trimSelection);
    useEffect(() => { trimSelectionRef.current = trimSelection; }, [trimSelection]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) setVisibleWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Reset zoom whenever a different recording loads (duration change signals a new clip).
    useEffect(() => { setZoom(1); }, [duration]);

    const zoomAround = (clientX, factor) => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;
        const rect = scrollEl.getBoundingClientRect();
        const anchorPx = clientX - rect.left + scrollEl.scrollLeft;
        const oldWidth = Math.max(1, width);
        const ratio = anchorPx / oldWidth;
        setZoom(z => {
            const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor));
            const newWidth = Math.round(visibleWidth * nextZoom);
            requestAnimationFrame(() => {
                if (!scrollRef.current) return;
                scrollRef.current.scrollLeft = ratio * newWidth - (clientX - rect.left);
            });
            return nextZoom;
        });
    };

    // Trackpad pinch is reported by the browser as a wheel event with ctrlKey
    // set — hijack that to zoom, and leave plain two-finger scroll (panning)
    // to the container's native horizontal scrolling. Added as a native
    // listener (not React's onWheel) so preventDefault reliably blocks the
    // browser's own page-zoom gesture.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleNativeWheel = (e) => {
            if (!e.ctrlKey || !duration) return;
            e.preventDefault();
            const factor = Math.exp(-e.deltaY * 0.02);
            zoomAround(e.clientX, factor);
        };
        el.addEventListener("wheel", handleNativeWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleNativeWheel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration, visibleWidth, width]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || width <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = WAVE_HEIGHT * dpr;
        canvas.style.width = width + "px";
        canvas.style.height = WAVE_HEIGHT + "px";
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, WAVE_HEIGHT);

        ctx.fillStyle = "#f7f9fb";
        ctx.fillRect(0, 0, width, WAVE_HEIGHT);

        if (!duration) {
            ctx.fillStyle = "#aaa";
            ctx.font = "12px Quicksand, sans-serif";
            ctx.fillText("Upload a recording to see the waveform", 14, WAVE_HEIGHT / 2 + 4);
            return;
        }

        (segments || []).forEach((seg, i) => {
            const x0 = (seg.start / duration) * width;
            const x1 = (seg.end / duration) * width;
            ctx.fillStyle = i === activeSegIdx ? "#2a9d8f26" : (i % 2 === 0 ? "#2a9d8f10" : "#2a9d8f05");
            ctx.fillRect(x0, 0, Math.max(1, x1 - x0), WAVE_HEIGHT);
        });

        if (peaks && peaks.length) {
            const mid = WAVE_HEIGHT / 2;
            const barW = width / peaks.length;
            ctx.fillStyle = TEAL;
            peaks.forEach((p, i) => {
                const h = Math.max(1.5, p * (WAVE_HEIGHT * 0.42));
                const x = i * barW;
                ctx.fillRect(x, mid - h, Math.max(1, barW - 1), h * 2);
            });
        } else {
            // No amplitude data (legacy recording, waveform unavailable) — still
            // draw a flat guide so parts/boundaries/seeking remain usable.
            ctx.fillStyle = "#2a9d8f30";
            ctx.fillRect(0, WAVE_HEIGHT / 2 - 1.5, width, 3);
        }

        ctx.lineWidth = 2;
        for (let i = 0; i < (segments || []).length - 1; i++) {
            const x = (segments[i].end / duration) * width;
            ctx.strokeStyle = "#fff";
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, WAVE_HEIGHT);
            ctx.stroke();
            ctx.fillStyle = RED;
            ctx.beginPath();
            ctx.arc(x, WAVE_HEIGHT / 2, 5, 0, Math.PI * 2);
            ctx.fill();
        }

        if (currentTime >= 0) {
            const px = (currentTime / duration) * width;
            ctx.strokeStyle = "#c0392b";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(px, 0);
            ctx.lineTo(px, WAVE_HEIGHT);
            ctx.stroke();
        }

        if (trimMode && trimSelection && trimSelection.end > trimSelection.start) {
            const x0 = (trimSelection.start / duration) * width;
            const x1 = (trimSelection.end / duration) * width;
            ctx.fillStyle = "rgba(192, 57, 43, 0.22)";
            ctx.fillRect(x0, 0, Math.max(1, x1 - x0), WAVE_HEIGHT);
            ctx.strokeStyle = "#c0392b";
            ctx.lineWidth = 2;
            [x0, x1].forEach(x => {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, WAVE_HEIGHT);
                ctx.stroke();
            });
        }
    }, [peaks, duration, segments, currentTime, width, activeSegIdx, trimMode, trimSelection]);

    const ratioFromClientX = (clientX) => {
        const rect = containerRef.current.getBoundingClientRect();
        return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    };

    const findNearestBoundary = (clientX) => {
        if (!duration || !segments || segments.length < 2) return null;
        const rect = containerRef.current.getBoundingClientRect();
        const xPx = clientX - rect.left;
        let nearest = null, nearestDist = Infinity;
        for (let i = 0; i < segments.length - 1; i++) {
            const bx = (segments[i].end / duration) * rect.width;
            const d = Math.abs(bx - xPx);
            if (d < nearestDist) { nearestDist = d; nearest = i; }
        }
        return nearestDist < 10 ? nearest : null;
    };

    const handleMouseDown = (e) => {
        if (!duration) return;
        if (trimMode) {
            const rect = containerRef.current.getBoundingClientRect();
            const xPx = e.clientX - rect.left;
            const sel = trimSelectionRef.current;
            // Anchor the drag to the edge NOT being grabbed (or to the click
            // point for a brand-new selection) and let the moving edge freely
            // cross it — the selection just flips instead of getting stuck,
            // so a handle can be dragged in either direction.
            let anchor = null;
            if (sel && sel.end > sel.start) {
                const sxPx = (sel.start / duration) * rect.width;
                const exPx = (sel.end / duration) * rect.width;
                if (Math.abs(xPx - sxPx) < 8) anchor = sel.end;
                else if (Math.abs(xPx - exPx) < 8) anchor = sel.start;
            }
            if (anchor === null) anchor = ratioFromClientX(e.clientX) * duration;
            draggingRef.current = { type: "trimDrag", anchor };
            const t = ratioFromClientX(e.clientX) * duration;
            onTrimSelectionChange({ start: Math.min(anchor, t), end: Math.max(anchor, t) });
            e.preventDefault();
            return;
        }
        const boundaryIdx = findNearestBoundary(e.clientX);
        if (boundaryIdx !== null) {
            draggingRef.current = { type: "boundary", idx: boundaryIdx };
            e.preventDefault();
        }
    };

    const handleClick = (e) => {
        if (trimMode) return;
        if (draggingRef.current !== null) return;
        if (findNearestBoundary(e.clientX) !== null) return;
        if (!duration) return;
        onSeek(ratioFromClientX(e.clientX) * duration);
    };

    useEffect(() => {
        const handleMove = (e) => {
            const d = draggingRef.current;
            if (!d || !duration) return;
            const t = ratioFromClientX(e.clientX) * duration;
            if (d.type === "boundary") {
                onDragBoundary(d.idx, t);
            } else if (d.type === "trimDrag") {
                onTrimSelectionChange({ start: Math.min(d.anchor, t), end: Math.max(d.anchor, t) });
            }
        };
        const handleUp = () => {
            if (draggingRef.current) {
                const wasBoundary = draggingRef.current.type === "boundary";
                draggingRef.current = null;
                if (wasBoundary) onDragEnd();
            }
        };
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, [duration, onDragBoundary, onDragEnd, onTrimSelectionChange]); // eslint-disable-line react-hooks/exhaustive-deps

    const zoomFromCenter = (factor) => {
        const rect = scrollRef.current.getBoundingClientRect();
        zoomAround(rect.left + rect.width / 2, factor);
    };

    const resetZoom = () => {
        setZoom(MIN_ZOOM);
        if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    };

    return (
        <div style={{ position: "relative" }}>
            <div
                ref={scrollRef}
                style={{ width: "100%", overflowX: "auto", overflowY: "hidden", borderRadius: 12, border: "1.5px solid #E0E0E0" }}
            >
                <div
                    ref={containerRef}
                    onMouseDown={handleMouseDown}
                    onClick={handleClick}
                    style={{ width, cursor: duration ? (trimMode ? "crosshair" : "pointer") : "default" }}
                >
                    <canvas ref={canvasRef} />
                </div>
            </div>
            {!!duration && (
                <div style={{
                    position: "absolute", top: 6, right: 6, display: "flex", alignItems: "center", gap: 3,
                    background: "#ffffffd9", borderRadius: 8, padding: 2, border: "1px solid #E0E0E0",
                }}>
                    <MiniBtn onClick={() => zoomFromCenter(1 / 1.5)} disabled={zoom <= MIN_ZOOM} title="Zoom out (or pinch on trackpad)">
                        <FiZoomOut size={12} />
                    </MiniBtn>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#888", minWidth: 28, textAlign: "center" }}>
                        {Math.round(zoom * 100)}%
                    </span>
                    <MiniBtn onClick={() => zoomFromCenter(1.5)} disabled={zoom >= MAX_ZOOM} title="Zoom in (or pinch on trackpad)">
                        <FiZoomIn size={12} />
                    </MiniBtn>
                    {zoom > MIN_ZOOM && (
                        <MiniBtn onClick={resetZoom} title="Reset zoom">
                            <FiX size={12} />
                        </MiniBtn>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main editor ─────────────────────────────────────────────────────
function VoiceoverEditor({
    audioUrl, segments, script, lessonKey,
    peaks: cachedPeaks, duration: cachedDuration,
    onAudioChange, onSegmentsChange, onPeaksChange,
}) {
    const safeSegments = segments || [];
    const [localSegments, setLocalSegments] = useState(safeSegments);
    const [peaks, setPeaks] = useState(null);
    const [duration, setDuration] = useState(0);
    const [waveformUnavailable, setWaveformUnavailable] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [uploadingAudio, setUploadingAudio] = useState(false);
    const [uploadingImageId, setUploadingImageId] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [previewingSegIdx, setPreviewingSegIdx] = useState(null);
    const [trimMode, setTrimMode] = useState(false);
    const [trimSelection, setTrimSelection] = useState(null);
    const [trimBusy, setTrimBusy] = useState(false);

    const audioRef = useRef(null);
    const draggingActiveRef = useRef(false);
    const localSegmentsRef = useRef(localSegments);
    const decodedUrlRef = useRef(null);
    // Caches the decoded AudioBuffer (real PCM samples, not just display peaks)
    // for the current audioUrl so trim/cut operations don't need to re-fetch.
    const rawBufferRef = useRef(null);

    useEffect(() => { localSegmentsRef.current = localSegments; }, [localSegments]);

    // Sync local mirror from parent unless a drag is currently in progress.
    useEffect(() => {
        if (!draggingActiveRef.current) setLocalSegments(safeSegments);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segments]);

    // Load the waveform for an already-uploaded recording. Prefer the
    // peaks/duration cached in Firestore at upload time (computed locally
    // from the File, no network round-trip involved) and only fall back to
    // fetchAudioArrayBuffer (direct fetch, then the Cloud Function proxy)
    // for older recordings saved before that caching existed.
    useEffect(() => {
        if (!audioUrl || decodedUrlRef.current === audioUrl) return;
        setWaveformUnavailable(false);

        if (cachedPeaks && cachedPeaks.length && cachedDuration) {
            setPeaks(cachedPeaks);
            setDuration(cachedDuration);
            decodedUrlRef.current = audioUrl;
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const buf = await fetchAudioArrayBuffer(audioUrl);
                const { peaks: p, duration: d, audioBuffer } = await decodeAudioArrayBuffer(buf);
                if (!cancelled) {
                    setPeaks(p);
                    setDuration(d);
                    decodedUrlRef.current = audioUrl;
                    rawBufferRef.current = { url: audioUrl, buffer: audioBuffer };
                    onPeaksChange(p, d); // backfill the cache so this never needs a remote fetch again
                }
            } catch {
                if (!cancelled) {
                    setPeaks(null);
                    setWaveformUnavailable(true);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [audioUrl, cachedPeaks, cachedDuration]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-create a single full-length part once we know the duration and none exist yet.
    useEffect(() => {
        if (audioUrl && duration > 0 && safeSegments.length === 0) {
            onSegmentsChange([{ id: uid(), start: 0, end: duration, text: script || "", emphasisText: "", imageUrl: "" }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioUrl, duration]);

    const activeSegIdx = localSegments.findIndex(s => currentTime >= s.start && currentTime < s.end);

    const commit = useCallback((next) => {
        setLocalSegments(next);
        onSegmentsChange(next);
    }, [onSegmentsChange]);

    const handleFileSelect = async (file) => {
        if (!file) return;
        setErrorMsg("");
        if (audioUrl && !window.confirm("Uploading a new recording will reset your part splits. Continue?")) {
            return;
        }
        let p, d, rawBuf;
        try {
            const buf = await file.arrayBuffer();
            ({ peaks: p, duration: d, audioBuffer: rawBuf } = await decodeAudioArrayBuffer(buf));
            setPeaks(p);
            setDuration(d);
            setWaveformUnavailable(false);
            setTrimMode(false);
            setTrimSelection(null);
            commit([{ id: uid(), start: 0, end: d, text: script || "", emphasisText: "", imageUrl: "" }]);
        } catch {
            setErrorMsg("Could not read that audio file. Try an MP3, WAV, or M4A recording.");
            return;
        }
        setUploadingAudio(true);
        try {
            const url = await uploadAudioFile(file, lessonKey);
            decodedUrlRef.current = url;
            rawBufferRef.current = { url, buffer: rawBuf };
            onAudioChange(url);
            onPeaksChange(p, d); // cache now so reopening this lesson never needs a remote re-fetch
        } catch (err) {
            setErrorMsg("Upload failed: " + (err?.message || "unknown error"));
        } finally {
            setUploadingAudio(false);
        }
    };

    const handleImageSelect = async (segId, file) => {
        if (!file) return;
        setUploadingImageId(segId);
        setErrorMsg("");
        try {
            const url = await uploadImageFile(file, lessonKey, segId);
            const next = localSegmentsRef.current.map(s => s.id === segId ? { ...s, imageUrl: url } : s);
            commit(next);
        } catch (err) {
            setErrorMsg("Image upload failed: " + (err?.message || "unknown error"));
        } finally {
            setUploadingImageId(null);
        }
    };

    const updateSegment = (i, patch) => {
        commit(localSegments.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    };

    const removeSegment = (i) => {
        if (localSegments.length <= 1) return;
        const updated = [...localSegments];
        if (i === 0) {
            updated[1] = { ...updated[1], start: updated[0].start };
            updated.splice(0, 1);
        } else {
            updated[i - 1] = { ...updated[i - 1], end: updated[i].end };
            updated.splice(i, 1);
        }
        commit(updated);
    };

    const splitAt = (time) => {
        const idx = localSegments.findIndex(s => time > s.start + 0.15 && time < s.end - 0.15);
        if (idx === -1) return;
        const seg = localSegments[idx];
        const updated = [...localSegments];
        updated[idx] = { ...seg, end: time };
        updated.splice(idx + 1, 0, { id: uid(), start: time, end: seg.end, text: "", emphasisText: "", imageUrl: "" });
        commit(updated);
    };

    const resetToOnePart = () => {
        if (!duration) return;
        if (!window.confirm("This will delete all part splits and merge everything into one part. Continue?")) return;
        commit([{ id: uid(), start: 0, end: duration, text: script || "", emphasisText: "", imageUrl: "" }]);
    };

    // Returns the real decoded audio (PCM samples) for the current recording,
    // fetching + decoding it on demand if this is the first trim this session.
    const ensureRawBuffer = useCallback(async () => {
        if (rawBufferRef.current && rawBufferRef.current.url === audioUrl) {
            return rawBufferRef.current.buffer;
        }
        const buf = await fetchAudioArrayBuffer(audioUrl);
        const { audioBuffer } = await decodeAudioArrayBuffer(buf);
        rawBufferRef.current = { url: audioUrl, buffer: audioBuffer };
        return audioBuffer;
    }, [audioUrl]);

    // Cuts the given [start,end] second ranges out of the audio, re-uploads
    // the trimmed clip, and shifts existing parts to match — never adds a part.
    const applyRemoval = async (ranges) => {
        setErrorMsg("");
        setTrimBusy(true);
        try {
            const buffer = await ensureRawBuffer();
            const removed = ranges.reduce((s, r) => s + (r.end - r.start), 0);
            if (removed >= buffer.duration - 0.1) {
                setErrorMsg("That would remove the entire recording — adjust the selection.");
                return;
            }
            const newBuffer = removeRangesFromBuffer(buffer, ranges);
            const wavBlob = encodeWav(newBuffer);
            const file = new File([wavBlob], `trimmed_${Date.now()}.wav`, { type: "audio/wav" });
            setUploadingAudio(true);
            const url = await uploadAudioFile(file, lessonKey);
            const { peaks: p } = computePeaksFromBuffer(newBuffer, 400);
            const newDuration = newBuffer.duration;

            decodedUrlRef.current = url;
            rawBufferRef.current = { url, buffer: newBuffer };
            setPeaks(p);
            setDuration(newDuration);
            setWaveformUnavailable(false);
            setCurrentTime(0);
            onAudioChange(url);
            onPeaksChange(p, newDuration);

            const remapped = remapSegmentsAfterRemoval(localSegmentsRef.current, ranges);
            commit(remapped.length ? remapped : [{ id: uid(), start: 0, end: newDuration, text: script || "", emphasisText: "", imageUrl: "" }]);

            setTrimMode(false);
            setTrimSelection(null);
        } catch (err) {
            setErrorMsg("Trim failed: " + (err?.message || "could not process that audio"));
        } finally {
            setTrimBusy(false);
            setUploadingAudio(false);
        }
    };

    const cutSelection = () => {
        if (!trimSelection || trimSelection.end - trimSelection.start < 0.05) return;
        const len = trimSelection.end - trimSelection.start;
        if (!window.confirm(`Remove the selected ${formatTime(len)} from the recording? The parts below will shift to match.`)) return;
        applyRemoval([trimSelection]);
    };

    const autoTrimSilence = async () => {
        setErrorMsg("");
        setTrimBusy(true);
        try {
            const buffer = await ensureRawBuffer();
            const edges = detectSilenceEdges(buffer);
            if (!edges) {
                setErrorMsg("Couldn't detect any spoken audio to trim to — the recording may be silent.");
                return;
            }
            const ranges = [];
            if (edges.start > 0.05) ranges.push({ start: 0, end: edges.start });
            if (edges.end < buffer.duration - 0.05) ranges.push({ start: edges.end, end: buffer.duration });
            if (!ranges.length) {
                setErrorMsg("No leading or trailing silence found to trim.");
                return;
            }
            await applyRemoval(ranges);
        } catch (err) {
            setErrorMsg("Couldn't analyze the recording: " + (err?.message || "unknown error"));
        } finally {
            setTrimBusy(false);
        }
    };

    const toggleTrimMode = () => {
        setTrimMode(m => !m);
        setTrimSelection(null);
    };

    const seekTo = (t) => {
        if (audioRef.current) audioRef.current.currentTime = t;
        setCurrentTime(t);
    };

    // Space toggles play/pause anywhere on the editor (matches most audio/video
    // editors) instead of the browser's default of scrolling the page — that
    // default only kicks in because clicking the waveform canvas leaves focus
    // on the body, since canvas isn't natively focusable.
    useEffect(() => {
        if (!audioUrl) return;
        const handleKeyDown = (e) => {
            if (e.code !== "Space" && e.key !== " ") return;
            const tag = e.target?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON" || e.target?.isContentEditable) return;
            const audio = audioRef.current;
            if (!audio) return;
            e.preventDefault();
            if (audio.paused) audio.play();
            else audio.pause();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [audioUrl]);

    const playSegment = (i) => {
        const seg = localSegments[i];
        if (!seg || !audioRef.current) return;
        audioRef.current.currentTime = seg.start;
        setPreviewingSegIdx(i);
        audioRef.current.play();
    };

    const handleTimeUpdate = () => {
        const t = audioRef.current.currentTime;
        setCurrentTime(t);
        if (previewingSegIdx !== null) {
            const seg = localSegments[previewingSegIdx];
            if (seg && t >= seg.end) {
                audioRef.current.pause();
                setPreviewingSegIdx(null);
            }
        }
    };

    const handleDragBoundary = useCallback((idx, time) => {
        draggingActiveRef.current = true;
        setLocalSegments(prev => {
            const seg = prev[idx], next = prev[idx + 1];
            if (!seg || !next) return prev;
            const minT = seg.start + 0.15;
            const maxT = next.end - 0.15;
            const clamped = Math.min(maxT, Math.max(minT, time));
            const updated = [...prev];
            updated[idx] = { ...seg, end: clamped };
            updated[idx + 1] = { ...next, start: clamped };
            return updated;
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        if (draggingActiveRef.current) {
            draggingActiveRef.current = false;
            onSegmentsChange(localSegmentsRef.current);
        }
    }, [onSegmentsChange]);

    return (
        <div>
            <audio
                ref={audioRef}
                src={audioUrl || undefined}
                controls={!!audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => { if (!duration && audioRef.current) setDuration(audioRef.current.duration); }}
                style={{ width: "100%", marginBottom: 10, display: audioUrl ? "block" : "none" }}
            />

            {!audioUrl && (
                <label style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                    border: "1.5px dashed #2a9d8f", borderRadius: 14, padding: "28px 16px", cursor: "pointer", color: TEAL,
                    background: "#2a9d8f08", marginBottom: 10,
                }}>
                    {uploadingAudio ? <Spinner size={20} /> : <FiUpload size={22} />}
                    <span style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13 }}>
                        {uploadingAudio ? "Uploading recording…" : "Upload your voice recording (mp3, wav, m4a)"}
                    </span>
                    <input type="file" accept="audio/*" hidden disabled={uploadingAudio} onChange={e => handleFileSelect(e.target.files[0])} />
                </label>
            )}

            {errorMsg && (
                <div style={{ background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 10, padding: "8px 12px", color: "#c0392b", fontWeight: 600, fontSize: 12, marginBottom: 10 }}>
                    {errorMsg}
                </div>
            )}

            {waveformUnavailable && (
                <div style={{ background: "#fff8e6", border: "1px solid #f2dfa8", borderRadius: 10, padding: "8px 12px", color: "#8a6d1f", fontWeight: 600, fontSize: 12, marginBottom: 10 }}>
                    Waveform preview isn't available for this recording (it was uploaded before waveforms were cached). Playback and the parts below still work — use "Replace Recording" with the same file to restore the waveform.
                </div>
            )}

            {audioUrl && (
                <>
                    <Waveform
                        peaks={peaks}
                        duration={duration}
                        segments={localSegments}
                        currentTime={currentTime}
                        activeSegIdx={activeSegIdx}
                        onSeek={seekTo}
                        onDragBoundary={handleDragBoundary}
                        onDragEnd={handleDragEnd}
                        trimMode={trimMode}
                        trimSelection={trimSelection}
                        onTrimSelectionChange={setTrimSelection}
                    />

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#888" }}>
                            {formatTime(currentTime)} / {formatTime(duration)}
                            {trimMode ? " · drag on the waveform to select audio to cut out" : " · drag the red dots to adjust splits"}
                        </span>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {!trimMode && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => splitAt(currentTime)}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: `1.5px solid ${TEAL}`, background: "#fff", color: TEAL, cursor: "pointer" }}
                                    >
                                        <FiScissors size={12} /> Split Here
                                    </button>
                                    <button
                                        type="button"
                                        onClick={autoTrimSilence}
                                        disabled={trimBusy || uploadingAudio}
                                        title="Automatically cut silence from the start and end of the recording"
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "#fff", color: RED, cursor: (trimBusy || uploadingAudio) ? "not-allowed" : "pointer", opacity: (trimBusy || uploadingAudio) ? 0.5 : 1 }}
                                    >
                                        {trimBusy ? <Spinner size={12} /> : <FiCrop size={12} />} Trim Silence
                                    </button>
                                    <button
                                        type="button"
                                        onClick={toggleTrimMode}
                                        disabled={trimBusy || uploadingAudio}
                                        title="Manually select a range of the recording to cut out"
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: "1.5px solid #d0d0d0", background: "#f0f0f0", color: "#333", cursor: (trimBusy || uploadingAudio) ? "not-allowed" : "pointer", opacity: (trimBusy || uploadingAudio) ? 0.5 : 1 }}
                                    >
                                        <FiCrop size={12} /> Trim Clip…
                                    </button>
                                    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: "1.5px solid #d0d0d0", background: "#f0f0f0", color: "#333", cursor: uploadingAudio ? "not-allowed" : "pointer" }}>
                                        {uploadingAudio ? <Spinner size={12} /> : <FiUpload size={12} />} Replace Recording
                                        <input type="file" accept="audio/*" hidden disabled={uploadingAudio} onChange={e => handleFileSelect(e.target.files[0])} />
                                    </label>
                                    <button
                                        type="button"
                                        onClick={resetToOnePart}
                                        disabled={localSegments.length <= 1}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: "1.5px solid #d0d0d0", background: "#f0f0f0", color: "#333", cursor: localSegments.length <= 1 ? "not-allowed" : "pointer", opacity: localSegments.length <= 1 ? 0.4 : 1 }}
                                    >
                                        <FiRefreshCw size={12} /> Reset
                                    </button>
                                </>
                            )}
                            {trimMode && (
                                <>
                                    <button
                                        type="button"
                                        onClick={cutSelection}
                                        disabled={trimBusy || !trimSelection || trimSelection.end - trimSelection.start < 0.05}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: `1.5px solid ${RED}`, background: RED, color: "#fff", cursor: (trimBusy || !trimSelection) ? "not-allowed" : "pointer", opacity: (trimBusy || !trimSelection || trimSelection.end - trimSelection.start < 0.05) ? 0.5 : 1 }}
                                    >
                                        {trimBusy ? <Spinner size={12} /> : <FiScissors size={12} />}
                                        {trimSelection && trimSelection.end - trimSelection.start >= 0.05
                                            ? `Cut ${formatTime(trimSelection.end - trimSelection.start)}`
                                            : "Cut Selection"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={toggleTrimMode}
                                        disabled={trimBusy}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, fontFamily: "'Quicksand', sans-serif", padding: "6px 12px", borderRadius: 10, border: "1.5px solid #d0d0d0", background: "#f0f0f0", color: "#333", cursor: trimBusy ? "not-allowed" : "pointer" }}
                                    >
                                        <FiX size={12} /> Cancel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#606060", display: "block", marginBottom: 8 }}>
                        {localSegments.length} Part{localSegments.length === 1 ? "" : "s"}
                    </label>
                    {localSegments.map((seg, i) => (
                        <div
                            key={seg.id}
                            style={{
                                border: `1.5px solid ${i === activeSegIdx ? TEAL : "#E0E0E0"}`,
                                background: i === activeSegIdx ? "#f0faf9" : "#fff",
                                borderRadius: 14, padding: 12, marginBottom: 8,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: TEAL, fontFamily: "'Quicksand', sans-serif" }}>
                                    Part {i + 1} · {formatTime(seg.start)}–{formatTime(seg.end)}
                                </span>
                                <div style={{ display: "flex", gap: 5 }}>
                                    <MiniBtn onClick={() => playSegment(i)} title="Preview this part" active={previewingSegIdx === i}>
                                        {previewingSegIdx === i ? <FiPause size={12} /> : <FiPlay size={12} />}
                                    </MiniBtn>
                                    <MiniBtn
                                        onClick={() => splitAt(currentTime)}
                                        disabled={!(currentTime > seg.start + 0.15 && currentTime < seg.end - 0.15)}
                                        title="Split this part at the current playhead position"
                                    >
                                        <FiScissors size={12} />
                                    </MiniBtn>
                                    <MiniBtn onClick={() => removeSegment(i)} disabled={localSegments.length <= 1} title="Delete this part (merges into neighbor)">
                                        <FiTrash2 size={12} />
                                    </MiniBtn>
                                </div>
                            </div>
                            <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9b59b6", display: "block", marginBottom: 4 }}>
                                Big Text (optional — not part of the script)
                            </label>
                            <input
                                type="text"
                                value={seg.emphasisText || ""}
                                onChange={e => updateSegment(i, { emphasisText: e.target.value })}
                                placeholder="A short word or phrase shown large, e.g. a vocab word"
                                style={{
                                    ...INPUT_STYLE,
                                    fontSize: 20,
                                    fontWeight: 700,
                                    color: "#9b59b6",
                                    border: "1.5px solid #9b59b620",
                                    background: "#9b59b608",
                                    marginBottom: 8,
                                }}
                                onFocus={e => e.target.style.borderColor = "#9b59b6"}
                                onBlur={e => e.target.style.borderColor = "#9b59b620"}
                            />
                            <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#606060", display: "block", marginBottom: 4 }}>
                                Script Text
                            </label>
                            <textarea
                                value={seg.text || ""}
                                onChange={e => updateSegment(i, { text: e.target.value })}
                                rows={2}
                                placeholder="Script text spoken during this part…"
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = TEAL}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                            />
                            <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
                                {seg.imageUrl ? (
                                    <div style={{ position: "relative", flexShrink: 0 }}>
                                        <img src={seg.imageUrl} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 10, border: "1px solid #E0E0E0", display: "block" }} onError={e => { e.target.style.opacity = 0.3; }} />
                                        <button
                                            type="button"
                                            onClick={() => updateSegment(i, { imageUrl: "" })}
                                            title="Remove image"
                                            style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: RED, color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                        >
                                            <FiX size={11} />
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ width: 56, height: 56, borderRadius: 10, border: "1.5px dashed #ccc", display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb", flexShrink: 0 }}>
                                        <FiImage size={18} />
                                    </div>
                                )}
                                <label style={{
                                    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700,
                                    fontFamily: "'Quicksand', sans-serif", padding: "7px 12px", borderRadius: 10,
                                    border: "1.5px dashed #2a9d8f", color: TEAL, cursor: uploadingImageId === seg.id ? "not-allowed" : "pointer", background: "#fff",
                                }}>
                                    {uploadingImageId === seg.id ? <Spinner size={12} /> : <FiUpload size={12} />}
                                    {seg.imageUrl ? "Change image" : "Add image"}
                                    <input type="file" accept="image/*" hidden disabled={uploadingImageId === seg.id} onChange={e => handleImageSelect(seg.id, e.target.files[0])} />
                                </label>
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

export default VoiceoverEditor;
